import apiClient from './axios';
import { clearStoredTokens } from '@/store/authStore';

// ============================================================================
// SHARED TYPES & UTILITIES
// ============================================================================

export interface PaginationQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  [key: string]: unknown;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

function buildParams(query?: PaginationQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const str = params.toString();
  return str ? `?${str}` : '';
}

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  register: async (data: { email: string; password: string; name: string }) => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },

  login: async (data: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }) => {
    const response = await apiClient.post('/auth/login', data);
    return response.data;
  },

  /** Valida el token de invitación (público) → { email, name }. */
  validateInvite: async (
    token: string,
  ): Promise<{ email: string; name: string }> => {
    const response = await apiClient.get(`/auth/invite/${token}`);
    return response.data;
  },

  /** Fija la contraseña desde una invitación (público). */
  setPassword: async (token: string, password: string) => {
    const response = await apiClient.post('/auth/set-password', {
      token,
      password,
    });
    return response.data;
  },

  googleLogin: async (idToken: string, rememberMe?: boolean) => {
    const response = await apiClient.post('/auth/google', {
      idToken,
      rememberMe,
    });
    return response.data;
  },

  /** Revoca la sesión server-side (cookie httpOnly) y limpia el cliente.
   *  Devuelve la URL de logout global del central si la sesión era SSO. */
  logout: async (): Promise<string | null> => {
    let ssoLogoutUrl: string | null = null;
    try {
      const response = await apiClient.post<{ ssoLogoutUrl?: string }>('/auth/logout');
      ssoLogoutUrl = response.data?.ssoLogoutUrl ?? null;
    } catch {
      // Best-effort: sin red, la cookie expira sola y el access token muere en 15m.
    }
    clearStoredTokens();
    return ssoLogoutUrl;
  },

  /** Vuelve a la cuenta original desde una impersonación (usa la cookie
   *  httpOnly de refresh; el cliente no guarda ningún backup de tokens). */
  stopImpersonation: async () => {
    const response = await apiClient.post('/auth/impersonate/stop');
    return response.data;
  },

  getProfile: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  impersonate: async (userId: string) => {
    const response = await apiClient.post(`/auth/impersonate/${userId}`);
    return response.data;
  },
};

// ============================================================================
// USERS API
// ============================================================================

export const usersApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/users${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  create: async (data: {
    email: string;
    /** Opcional: si se omite, el usuario se crea sin contraseña y recibe un
     *  email de invitación para fijarla él mismo. */
    password?: string;
    name: string;
    roleIds?: string[];
    /** Anunciante vinculado (obligatorio si se asigna el rol ADVERTISER). */
    advertiserId?: string;
    brandIds?: string[];
    locationIds?: string[];
    isActive?: boolean;
  }) => {
    const response = await apiClient.post('/users', data);
    return response.data;
  },

  /** Reenvía el email de invitación (nuevo enlace para fijar contraseña). */
  resendInvite: async (id: string) => {
    const response = await apiClient.post(`/users/${id}/resend-invite`);
    return response.data;
  },

  update: async (
    id: string,
    data: {
      name?: string;
      email?: string;
      roleIds?: string[];
      /** Anunciante vinculado; null para desvincular. */
      advertiserId?: string | null;
      brandIds?: string[];
      locationIds?: string[];
      password?: string;
      isActive?: boolean;
      /** Mover de empresa (solo super-admin de plataforma). */
      tenantId?: string;
    }
  ) => {
    const response = await apiClient.patch(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },

  hardDelete: async (id: string) => {
    const response = await apiClient.delete(`/users/${id}/permanent`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/users/${id}/restore`);
    return response.data;
  },

  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.patch('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.patch('/users/me/password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// ============================================================================
// ROLES & PERMISSIONS API
// ============================================================================

export interface PermissionFlags {
  canView: boolean;
  /** Visibilidad en el sidebar/menú lateral, independiente de `canView`.
   *  `canView` permite leer/usar los datos y abrir la tabla; este flag solo
   *  decide si la feature aparece como entrada de menú. */
  canViewSidebar?: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove?: boolean;
  canIssue?: boolean;
  canExecute?: boolean;
}

export interface FeatureMeta {
  key: string;
  label: string;
  section?: string;
  actions: (keyof PermissionFlags)[];
}

export type PermissionsMap = Record<string, PermissionFlags>;

export const rolesApi = {
  /** Returns effective permissions for the current user (union of all their roles) */
  getMyPermissions: async (): Promise<PermissionsMap> => {
    const response = await apiClient.get('/roles/permissions/me');
    return response.data;
  },

  /** Returns all roles with all their permissions — for management UI.
   *  `tenantId` is required when the caller is super-admin and wants to
   *  scope to a specific tenant. */
  getAllPermissions: async (
    tenantId?: string,
  ): Promise<Record<string, PermissionsMap>> => {
    const response = await apiClient.get('/roles/permissions', {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /** Returns features with their labels and available actions */
  getFeatures: async (): Promise<FeatureMeta[]> => {
    const response = await apiClient.get('/roles/features');
    return response.data;
  },

  /** Update a specific role+feature permission in the targeted tenant. */
  updatePermission: async (
    role: string,
    feature: string,
    data: Partial<PermissionFlags>,
    tenantId?: string,
  ): Promise<void> => {
    await apiClient.put(`/roles/permissions/${role}/${feature}`, data, {
      params: tenantId ? { tenantId } : undefined,
    });
  },

  /** List all roles (built-in + custom) — super-admin only. */
  listRoles: async (
    tenantId?: string,
    opts: { includeInactive?: boolean; onlyInactive?: boolean } = {},
  ) => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenantId = tenantId;
    if (opts.includeInactive) params.includeInactive = 'true';
    if (opts.onlyInactive) params.onlyInactive = 'true';
    const response = await apiClient.get('/roles', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  /** List roles as options for user assignment — requires usuarios.canEdit */
  listRoleOptions: async () => {
    const response = await apiClient.get('/roles/options');
    return response.data;
  },

  /** Create a new custom role in the targeted tenant. */
  createRole: async (data: { name: string; label: string }, tenantId?: string) => {
    const response = await apiClient.post('/roles', data, {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /** Update a role's display label */
  updateRole: async (
    id: string,
    data: { label: string },
    tenantId?: string,
  ) => {
    const response = await apiClient.patch(`/roles/${id}`, data, {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /** Soft-delete (dar de baja) un rol */
  deleteRole: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}`, {
      params: tenantId ? { tenantId } : undefined,
    });
  },

  /** Restaura un rol soft-borrado */
  restoreRole: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.patch(
      `/roles/${id}/restore`,
      undefined,
      { params: tenantId ? { tenantId } : undefined },
    );
  },

  /** Borrado físico real de un rol previamente dado de baja */
  hardDeleteRole: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}/permanent`, {
      params: tenantId ? { tenantId } : undefined,
    });
  },
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================

export const notificationsApi = {
  getAll: async (query?: { page?: number; limit?: number; unreadOnly?: boolean }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.limit) params.set('limit', String(query.limit));
    if (query?.unreadOnly) params.set('unreadOnly', 'true');
    const qs = params.toString();
    const response = await apiClient.get(`/notifications${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get('/notifications/unread-count');
    return response.data;
  },

  markAsRead: async (id: string) => {
    const response = await apiClient.patch(`/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await apiClient.patch('/notifications/read-all');
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/notifications/${id}`);
    return response.data;
  },

  deleteMany: async (ids: string[]) => {
    const response = await apiClient.delete('/notifications/bulk', { data: { ids } });
    return response.data;
  },

  getPreferences: async () => {
    const response = await apiClient.get('/notifications/preferences');
    return response.data;
  },

  updatePreferences: async (
    preferences: Array<{ eventType: string; channel: string; enabled: boolean }>
  ) => {
    const response = await apiClient.put('/notifications/preferences', { preferences });
    return response.data;
  },

  // Admin — notification types
  adminListTypes: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    category?: string;
    severity?: string;
  }) => {
    const response = await apiClient.get('/notifications/admin/types', { params });
    return response.data;
  },

  adminCreateType: async (data: {
    eventKey: string;
    label: string;
    description?: string;
    severity?: string;
    category: string;
    icon?: string;
    color?: string;
  }) => {
    const response = await apiClient.post('/notifications/admin/types', data);
    return response.data;
  },

  adminUpdateType: async (
    id: string,
    data: {
      label?: string;
      description?: string;
      severity?: string;
      category?: string;
      icon?: string;
      color?: string;
      isActive?: boolean;
    }
  ) => {
    const response = await apiClient.patch(`/notifications/admin/types/${id}`, data);
    return response.data;
  },

  adminDeleteType: async (id: string) => {
    const response = await apiClient.delete(`/notifications/admin/types/${id}`);
    return response.data;
  },

  // Admin — notification rules
  adminListRules: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    notificationTypeId?: string;
  }) => {
    const response = await apiClient.get('/notifications/admin/rules', { params });
    return response.data;
  },

  adminCreateRule: async (data: {
    name: string;
    notificationTypeId: string;
    conditions?: unknown;
    titleTemplate: string;
    bodyTemplate?: string;
    severity?: string;
    recipientType: string;
    recipientConfig: Record<string, unknown>;
    excludeActor?: boolean;
    priority?: number;
  }) => {
    const response = await apiClient.post('/notifications/admin/rules', data);
    return response.data;
  },

  adminUpdateRule: async (
    id: string,
    data: {
      name?: string;
      conditions?: unknown;
      titleTemplate?: string;
      bodyTemplate?: string;
      severity?: string;
      recipientType?: string;
      recipientConfig?: Record<string, unknown>;
      excludeActor?: boolean;
      priority?: number;
      isActive?: boolean;
    }
  ) => {
    const response = await apiClient.patch(`/notifications/admin/rules/${id}`, data);
    return response.data;
  },

  adminDeleteRule: async (id: string) => {
    const response = await apiClient.delete(`/notifications/admin/rules/${id}`);
    return response.data;
  },

  adminTestRule: async (id: string, payload: Record<string, unknown>) => {
    const response = await apiClient.post(`/notifications/admin/rules/${id}/test`, { payload });
    return response.data;
  },

  adminSendTest: async (data: {
    ruleId?: string;
    title?: string;
    body?: string;
    severity?: string;
    channels?: string[];
  }) => {
    const response = await apiClient.post('/notifications/admin/send-test', data);
    return response.data;
  },
};

// ============================================================================
// MAINTENANCE & DATABASE API
// ============================================================================

export const maintenanceApi = {
  getLogHistory: async (params: {
    page?: number;
    limit?: number;
    level?: string;
    context?: string;
    search?: string;
    from?: string;
    to?: string;
  }): Promise<{
    data: { id: string; ts: string; level: string; context: string; message: string }[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.level) qs.set('level', params.level);
    if (params.context) qs.set('context', params.context);
    if (params.search) qs.set('search', params.search);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const response = await apiClient.get(`/maintenance/logs/history?${qs}`);
    return response.data;
  },
};

export const databaseApi = {
  listTables: async (): Promise<{ table_name: string; row_count: string }[]> => {
    const response = await apiClient.get('/database/tables');
    return response.data;
  },

  getColumns: async (
    table: string
  ): Promise<
    { column_name: string; data_type: string; is_nullable: string; column_default: string | null }[]
  > => {
    const response = await apiClient.get(`/database/tables/columns?table=${table}`);
    return response.data;
  },

  getTableData: async (
    table: string,
    page?: number,
    limit?: number
  ): Promise<{ data: Record<string, unknown>[]; total: number }> => {
    const params = new URLSearchParams({ table });
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));
    const response = await apiClient.get(`/database/tables/data?${params}`);
    return response.data;
  },

  executeQuery: async (
    sql: string
  ): Promise<{
    rows?: Record<string, unknown>[];
    rowCount?: number;
    duration?: number;
    type?: string;
    error?: string;
  }> => {
    const response = await apiClient.post('/database/query', { sql });
    return response.data;
  },
};

// ============================================================================
// USER SCHEDULED TASKS API (tareas configurables por usuario)
// ============================================================================

export const userScheduledTasksApi = {
  getAll: async (query?: {
    isActive?: boolean;
    actionType?: string;
    frequencyType?: string;
  }) => {
    const response = await apiClient.get('/scheduled-tasks/user', {
      params: query,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await apiClient.get(`/scheduled-tasks/user/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    actionType: string;
    actionConfig: Record<string, unknown>;
    frequencyType: 'WEEKLY' | 'MONTHLY';
    frequencyConfig: { dayOfWeek?: number; dayOfMonth?: number };
    executionTime: string;
  }) => {
    const response = await apiClient.post('/scheduled-tasks/user', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Record<string, unknown>>) => {
    const response = await apiClient.patch(
      `/scheduled-tasks/user/${id}`,
      data,
    );
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/scheduled-tasks/user/${id}`);
  },

  activate: async (id: string) => {
    const response = await apiClient.patch(
      `/scheduled-tasks/user/${id}/activate`,
    );
    return response.data;
  },

  deactivate: async (id: string) => {
    const response = await apiClient.patch(
      `/scheduled-tasks/user/${id}/deactivate`,
    );
    return response.data;
  },

  execute: async (id: string) => {
    const response = await apiClient.post(
      `/scheduled-tasks/user/${id}/execute`,
    );
    return response.data;
  },

  getExecutions: async (id: string) => {
    const response = await apiClient.get(
      `/scheduled-tasks/user/${id}/executions`,
    );
    return response.data;
  },
};

// ============================================================================
// AUDIT API — Timeline genérico de eventos por entidad
// ============================================================================

export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'RESTORE'
  | 'STATUS_CHANGE'
  | 'PHOTO_ADDED'
  | 'VERIFICATION_COMPLETED'
  | 'NOTE_ADDED'
  | 'ASSIGNMENT_DELIVERED'
  | 'ASSIGNMENT_RETURNED'
  | 'CHECKLIST_ITEM'
  | 'CUSTOM';

export interface AuditChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface AuditEntry {
  id: string;
  action: AuditAction;
  createdAt: string;
  user: { id: string; name: string; email: string; avatar?: string | null } | null;
  changes: AuditChange[] | null;
  metadata: Record<string, unknown> | null;
}

export interface AuditTimelineResponse {
  items: AuditEntry[];
  nextCursor: string | null;
}

export interface AuditListEntry extends AuditEntry {
  entityType: string;
  entityId: string;
}

export interface AuditListResponse {
  items: AuditListEntry[];
  nextCursor: string | null;
}

export interface AuditListParams {
  limit?: number;
  cursor?: string;
  actions?: AuditAction[];
  entityType?: string;
  userId?: string;
  from?: string;
  to?: string;
  search?: string;
}

export const auditApi = {
  getTimeline: async (
    entityType: string,
    entityId: string,
    params?: { limit?: number; cursor?: string; actions?: AuditAction[] },
  ): Promise<AuditTimelineResponse> => {
    const q: Record<string, string | number> = {};
    if (params?.limit) q.limit = params.limit;
    if (params?.cursor) q.cursor = params.cursor;
    if (params?.actions && params.actions.length > 0) {
      q.actions = params.actions.join(',');
    }
    const response = await apiClient.get(`/audit/${entityType}/${entityId}`, {
      params: q,
    });
    return response.data;
  },
  list: async (params?: AuditListParams): Promise<AuditListResponse> => {
    const q: Record<string, string | number> = {};
    if (params?.limit) q.limit = params.limit;
    if (params?.cursor) q.cursor = params.cursor;
    if (params?.actions && params.actions.length > 0) {
      q.actions = params.actions.join(',');
    }
    if (params?.entityType) q.entityType = params.entityType;
    if (params?.userId) q.userId = params.userId;
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    if (params?.search) q.search = params.search;
    const response = await apiClient.get('/audit', { params: q });
    return response.data;
  },
  listSystem: async (params?: AuditListParams): Promise<AuditListResponse> => {
    const q: Record<string, string | number> = {};
    if (params?.limit) q.limit = params.limit;
    if (params?.cursor) q.cursor = params.cursor;
    if (params?.actions && params.actions.length > 0) {
      q.actions = params.actions.join(',');
    }
    if (params?.entityType) q.entityType = params.entityType;
    if (params?.userId) q.userId = params.userId;
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    if (params?.search) q.search = params.search;
    const response = await apiClient.get('/audit/system', { params: q });
    return response.data;
  },
  getEntityTypes: async (): Promise<{ entityTypes: string[] }> => {
    const response = await apiClient.get('/audit/entity-types');
    return response.data;
  },
  getSystemEntityTypes: async (): Promise<{ entityTypes: string[] }> => {
    const response = await apiClient.get('/audit/system/entity-types');
    return response.data;
  },
};

// ============================================================================
// PLATFORM API (cross-tenant super-admin)
// ============================================================================

import type { Tenant } from '@/types';

export type TenantServiceMode = 'FULL' | 'MANAGED' | 'ASSIGNMENTS_ONLY';

export interface CreateTenantPayload {
  slug: string;
  name: string;
  adminEmail: string;
  adminName: string;
  adminPassword: string;
  serviceMode?: TenantServiceMode;
  managedById?: string;
}

export interface UpdateTenantPayload {
  name?: string;
  isActive?: boolean;
  serviceMode?: TenantServiceMode;
  managedById?: string | null;
}

export interface VisibleTenant {
  id: string;
  slug: string;
  name: string;
  serviceMode: TenantServiceMode;
  isOwn: boolean;
  isManaged: boolean;
}

export interface ImpersonateTenantResponse {
  accessToken: string;
  refreshToken: string;
  /**
   * Sigue siendo el super-admin (no cambia de identidad). El backend
   * sobrescribe `tenantId` en este `user` con el del tenant viewing
   * para que el frontend lo refleje en CLS-like state.
   */
  user: import('@/types').User;
  /**
   * Empresa que está visualizando ahora mismo. Distinto del tenant
   * "natural" del super-admin (Boosters). El banner usa este nombre.
   */
  viewingTenant: Tenant;
}

// ============================================================================
// SCHEDULED TASKS API (Sistema — tareas programadas)
// ============================================================================

export const scheduledTasksApi = {
  catalog: async (): Promise<import('@/types').ScheduledTaskCatalogItem[]> => {
    const r = await apiClient.get('/scheduled-tasks/catalog');
    return r.data;
  },
  getAll: async (params?: {
    isActive?: boolean;
    actionType?: string;
    isSystem?: boolean;
  }): Promise<import('@/types').ScheduledTask[]> => {
    const r = await apiClient.get('/scheduled-tasks', { params });
    return r.data;
  },
  getById: async (id: string): Promise<import('@/types').ScheduledTask> => {
    const r = await apiClient.get(`/scheduled-tasks/${id}`);
    return r.data;
  },
  create: async (
    data: Partial<import('@/types').ScheduledTask>,
  ): Promise<import('@/types').ScheduledTask> => {
    const r = await apiClient.post('/scheduled-tasks', data);
    return r.data;
  },
  update: async (
    id: string,
    data: Partial<import('@/types').ScheduledTask>,
  ): Promise<import('@/types').ScheduledTask> => {
    const r = await apiClient.patch(`/scheduled-tasks/${id}`, data);
    return r.data;
  },
  activate: async (id: string): Promise<import('@/types').ScheduledTask> => {
    const r = await apiClient.patch(`/scheduled-tasks/${id}/activate`);
    return r.data;
  },
  deactivate: async (id: string): Promise<import('@/types').ScheduledTask> => {
    const r = await apiClient.patch(`/scheduled-tasks/${id}/deactivate`);
    return r.data;
  },
  run: async (id: string): Promise<{ executionId: string; status: string }> => {
    const r = await apiClient.post(`/scheduled-tasks/${id}/run`);
    return r.data;
  },
  hardDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/scheduled-tasks/${id}`);
  },
  executions: async (
    id: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<{
    data: import('@/types').ScheduledTaskExecution[];
    meta: { total: number; page: number; pageSize: number; totalPages: number };
  }> => {
    const r = await apiClient.get(`/scheduled-tasks/${id}/executions`, {
      params,
    });
    return r.data;
  },
};

// ============================================================================
// INTEGRACIONES API (API keys del plugin WordPress / script)
// ============================================================================

export const integrationsApi = {
  /** Lista las API keys del workspace. */
  getKeys: async (): Promise<import('@/types').ApiKey[]> => {
    const r = await apiClient.get('/api-keys');
    return r.data;
  },
  /** Crea una key nueva acotada a un site (su etiqueta se autogenera del
   *  dominio en el backend). El `secret` solo viene en esta respuesta. */
  createKey: async (
    siteId: string,
  ): Promise<import('@/types').ApiKeyCreated> => {
    const r = await apiClient.post('/api-keys', { siteId });
    return r.data;
  },
  /** Activa una key previamente desactivada. */
  activateKey: async (id: string): Promise<import('@/types').ApiKey> => {
    const r = await apiClient.patch(`/api-keys/${id}/activate`);
    return r.data;
  },
  /** Desactiva una key (deja de autenticar hasta reactivarla). */
  deactivateKey: async (id: string): Promise<import('@/types').ApiKey> => {
    const r = await apiClient.patch(`/api-keys/${id}/deactivate`);
    return r.data;
  },
  /** Estado de conexión de la integración para una key (verificador). */
  status: async (id: string): Promise<import('@/types').IntegrationStatus> => {
    const r = await apiClient.get(`/api-keys/${id}/status`);
    return r.data;
  },
  /** Elimina definitivamente una key. El secreto deja de autenticar. */
  deleteKey: async (id: string): Promise<{ id: string }> => {
    const r = await apiClient.delete(`/api-keys/${id}`);
    return r.data;
  },
  /** Reveal puntual del secreto (requiere permiso de edición; queda auditado). */
  revealKey: async (id: string): Promise<{ id: string; secret: string }> => {
    const r = await apiClient.get(`/api-keys/${id}/secret`);
    return r.data;
  },
  /** Rota el secreto de la key (misma fila y site). El anterior deja de
   *  autenticar; el nuevo `secret` solo viene en esta respuesta. */
  rotateKey: async (id: string): Promise<import('@/types').ApiKeyCreated> => {
    const r = await apiClient.post(`/api-keys/${id}/rotate`);
    return r.data;
  },
};

// ============================================================================
// CAMPAIGNS API (AI Visibility)
// ============================================================================

export interface CreateCampaignInput {
  internalName: string;
  publisherVisibleName: string;
  advertiserId: string;
  language: import('@/types').CampaignLanguage;
  budget: number;
  startDate: string;
  endDate?: string;
  marginPct?: number;
}

export interface CreateGeosphereProjectInput {
  brandName: string;
  brandDomain: string;
  prompts: string[];
  country?: string;
  language?: string;
  sector?: string;
  displayName?: string;
  engines?: string[];
  triggerRun?: boolean;
}

export interface AisCampaignEntry {
  id: string;
  internalName: string;
  status: import('@/types').CampaignStatus;
  hasProject: boolean;
  /** SOA de la marca en el último run (0–100). null = sin proyecto, sin run
   *  o Geosphere no disponible. */
  soa: number | null;
  brandName: string | null;
  totalExecutions: number | null;
  lastRunAt: string | null;
}

export interface AisAdvertiserGroup {
  advertiserId: string | null;
  name: string;
  avgSoa: number | null;
  campaignCount: number;
  campaigns: AisCampaignEntry[];
}

export interface CampaignsAisOverview {
  advertisers: AisAdvertiserGroup[];
  overall: {
    avgSoa: number | null;
    campaignsWithData: number;
    totalCampaigns: number;
  };
}

export interface CampaignHistoryEntry {
  id: string;
  kind: 'campaign_status' | 'url';
  at: string;
  action: string;
  oldValue: string | null;
  newValue: string | null;
  notes: string | null;
  /** URL afectada (null en cambios de estado de la campaña). */
  url: string | null;
  /** Campo modificado (price, status…), null en altas/bajas. */
  field: string | null;
  userName: string | null;
}

export const campaignsApi = {
  getAll: async (
    query?: PaginationQuery,
  ): Promise<PaginatedResponse<import('@/types').Campaign>> => {
    const r = await apiClient.get(`/campaigns${buildParams(query)}`);
    return r.data;
  },
  getOne: async (id: string): Promise<import('@/types').Campaign> => {
    const r = await apiClient.get(`/campaigns/${id}`);
    return r.data;
  },
  /** Resumen de AIS de todas las campañas, agrupado por anunciante. */
  getAisOverview: async (): Promise<CampaignsAisOverview> => {
    const r = await apiClient.get('/campaigns/ais-overview');
    return r.data;
  },
  stats: async (): Promise<import('@/types').CampaignStats> => {
    const r = await apiClient.get('/campaigns/stats');
    return r.data;
  },
  create: async (
    data: CreateCampaignInput,
  ): Promise<import('@/types').Campaign> => {
    const r = await apiClient.post('/campaigns', data);
    return r.data;
  },
  update: async (
    id: string,
    data: Partial<CreateCampaignInput>,
  ): Promise<import('@/types').Campaign> => {
    const r = await apiClient.patch(`/campaigns/${id}`, data);
    return r.data;
  },
  transition: async (
    id: string,
    status: import('@/types').CampaignStatus,
    notes?: string,
  ): Promise<import('@/types').Campaign> => {
    const r = await apiClient.patch(`/campaigns/${id}/transition`, {
      status,
      notes,
    });
    return r.data;
  },
  // "Planificar": deja la campaña lista para autopublicarse. Si ya está en
  // periodo, la activa y publica sus URLs elegibles ahora; si es futura, la deja
  // programada para la tarea diaria del día de inicio.
  schedule: async (
    id: string,
  ): Promise<{
    status: import('@/types').CampaignStatus;
    published: number;
    skipped: number;
  }> => {
    const r = await apiClient.post(`/campaigns/${id}/schedule`);
    return r.data;
  },
  // Paso 2A: conectar proyecto Geosphere existente.
  connectGeosphere: async (
    id: string,
    geosphereProjectId: string,
  ): Promise<import('@/types').Campaign> => {
    const r = await apiClient.post(`/campaigns/${id}/geosphere/connect`, {
      geosphereProjectId,
    });
    return r.data;
  },
  // Paso 2B: crear proyecto Geosphere nuevo y vincularlo.
  createGeosphereProject: async (
    id: string,
    data: CreateGeosphereProjectInput,
  ): Promise<{
    campaign: import('@/types').Campaign;
    geosphere: { projectId: string; reused: boolean };
  }> => {
    const r = await apiClient.post(`/campaigns/${id}/geosphere/project`, data);
    return r.data;
  },
  // ── URLs de la campaña (paso de selección de URLs) ──
  listUrls: async (id: string): Promise<CampaignUrl[]> => {
    const r = await apiClient.get(`/campaigns/${id}/urls`);
    return r.data;
  },
  // Tráfico IA diario de la campaña (serie ai_search/ai_training) en su periodo.
  // `urlId` opcional: acota a una sola URL.
  traffic: async (id: string, urlId?: string): Promise<CampaignTraffic> => {
    const r = await apiClient.get(`/campaigns/${id}/traffic`, {
      params: urlId ? { urlId } : undefined,
    });
    return r.data;
  },
  // Estadísticas de tráfico IA por URL (respuestas/entrenamiento/total).
  trafficByUrl: async (id: string): Promise<CampaignTrafficByUrl> => {
    const r = await apiClient.get(`/campaigns/${id}/traffic-by-url`);
    return r.data;
  },
  addUrls: async (
    id: string,
    urls: AddUrlItem[],
  ): Promise<{
    added: number;
    skipped: number;
    /** URLs no añadidas por solape total de fechas con otra campaña. */
    conflicts: number;
    conflictUrls: string[];
  }> => {
    const r = await apiClient.post(`/campaigns/${id}/urls`, { urls });
    return r.data;
  },
  removeUrl: async (id: string, urlId: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${id}/urls/${urlId}`);
  },
  // Configuración de una URL: importe final, estado, texto IA editado.
  updateUrl: async (
    id: string,
    urlId: string,
    data: UpdateCampaignUrlInput,
  ): Promise<CampaignUrl> => {
    const r = await apiClient.patch(`/campaigns/${id}/urls/${urlId}`, data);
    return r.data;
  },
  // Genera el contenido IA de la URL y crea una versión en borrador.
  /** Desactiva una URL de campaña viva: deja de servirse, conserva historial. */
  /** Historial completo de la campaña (estado + URLs). Equipo Boosters. */
  history: async (
    id: string,
  ): Promise<{ entries: CampaignHistoryEntry[] }> => {
    const r = await apiClient.get(`/campaigns/${id}/history`);
    return r.data;
  },

  cancelUrl: async (id: string, urlId: string): Promise<void> => {
    await apiClient.post(`/campaigns/${id}/urls/${urlId}/cancel`);
  },

  generateUrlText: async (
    id: string,
    urlId: string,
  ): Promise<CampaignContentVersion> => {
    const r = await apiClient.post(
      `/campaigns/${id}/urls/${urlId}/generate-text`,
    );
    return r.data;
  },
  // Contenido de la URL (estado + versiones con su HTML); null si no hay.
  getUrlContent: async (
    id: string,
    urlId: string,
  ): Promise<CampaignContentInfo | null> => {
    const r = await apiClient.get(`/campaigns/${id}/urls/${urlId}/content`);
    return r.data;
  },
  // Publica una versión (la más reciente si no se indica versionId).
  publishUrlContent: async (
    id: string,
    urlId: string,
    versionId?: string,
  ): Promise<CampaignContentVersion> => {
    const r = await apiClient.post(
      `/campaigns/${id}/urls/${urlId}/content/publish`,
      versionId ? { versionId } : {},
    );
    return r.data;
  },
  // Edita a mano el texto del contenido de la URL (comercial): guarda una nueva
  // versión en borrador con el HTML editado.
  editUrlContent: async (
    id: string,
    urlId: string,
    html: string,
  ): Promise<CampaignContentVersion> => {
    const r = await apiClient.patch(`/campaigns/${id}/urls/${urlId}/content`, {
      html,
    });
    return r.data;
  },
  // Bajas (soft-delete) / reactivación / borrado físico, como anunciantes.
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${id}`);
  },
  restore: async (id: string): Promise<import('@/types').Campaign> => {
    const r = await apiClient.patch(`/campaigns/${id}/restore`);
    return r.data;
  },
  hardDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/campaigns/${id}/permanent`);
  },
  // Vista de campañas del editor (publisher): sus URLs listas, paginadas.
  listPublisherUrls: async (
    query?: PaginationQuery,
  ): Promise<
    PaginatedResponse<import('@/types').PublisherCampaignUrl>
  > => {
    const r = await apiClient.get(`/campaigns/publisher-urls${buildParams(query)}`);
    return r.data;
  },
  // Detalle de una URL del editor: datos + texto generado (para decidir).
  getPublisherUrl: async (
    urlId: string,
  ): Promise<import('@/types').PublisherCampaignUrlDetail> => {
    const r = await apiClient.get(`/campaigns/publisher-urls/${urlId}`);
    return r.data;
  },
  // Decisión del editor sobre una URL suya: aceptar o rechazar el texto.
  decidePublisherUrl: async (
    urlId: string,
    decision: 'accepted' | 'rejected',
  ): Promise<{ id: string; status: string }> => {
    const r = await apiClient.patch(
      `/campaigns/publisher-urls/${urlId}/decision`,
      { decision },
    );
    return r.data;
  },
  /** Alta de campaña desde una propuesta aceptada (hereda cliente/proyecto/URLs). */
  createFromProposal: async (data: {
    proposalId: string;
    internalName: string;
    publisherVisibleName: string;
    language?: 'es' | 'en';
    budget?: number;
    startDate: string;
    endDate?: string;
    marginPct?: number;
  }): Promise<import('@/types').Campaign> => {
    const r = await apiClient.post('/campaigns/from-proposal', data);
    return r.data;
  },
  // Candidatas a añadir (source-urls del proyecto) con su nota GEA (= propuestas).
  availableUrls: async (
    id: string,
  ): Promise<{ urls: CampaignAvailableUrl[] }> => {
    const r = await apiClient.get(`/campaigns/${id}/available-urls`);
    return r.data;
  },
  // Nota GEA ACTUAL por URL (datos de Geosphere de hoy), por id de URL.
  currentScores: async (
    id: string,
  ): Promise<{ computedAt: string; scores: Record<string, number | null> }> => {
    const r = await apiClient.get(`/campaigns/${id}/current-scores`);
    return r.data;
  },
  // URLs candidatas: las 30 primeras del Ecosistema Boosters aún no en la campaña,
  // con su nota GEA.
  candidateUrls: async (
    id: string,
  ): Promise<{ urls: CampaignCandidateUrl[] }> => {
    const r = await apiClient.get(`/campaigns/${id}/candidate-urls`);
    return r.data;
  },
  // Vista anunciante: URLs que llegaron a estar activas (URL + activa ahora).
  clientUrls: async (id: string): Promise<{ urls: CampaignClientUrl[] }> => {
    const r = await apiClient.get(`/campaigns/${id}/client-urls`);
    return r.data;
  },
};

/** Tráfico diario de una campaña (serie por día en el periodo de la campaña).
 *  `ai` = total de IA del día (aiSearch + aiTraining). `human` = visitas de
 *  usuarios. */
export interface CampaignTraffic {
  from: string;
  to: string;
  series: Array<{
    day: string;
    aiSearch: number;
    aiTraining: number;
    ai: number;
    human: number;
  }>;
  totalAiSearch: number;
  totalAiTraining: number;
  totalAi: number;
  totalHuman: number;
  /** Visitas de IA por operador (LLM), mismo periodo, de más a menos. El
   *  operador `unknown` agrupa las que el plugin no pudo atribuir. */
  operators: Array<{
    operator: string;
    aiSearch: number;
    aiTraining: number;
    ai: number;
  }>;
}

/** Estadísticas de tráfico IA agregadas por cada URL de la campaña. */
export interface CampaignTrafficByUrl {
  /** Inicio del periodo de la campaña (ISO). */
  from: string;
  /** Fin efectivo del periodo (fin de campaña o hoy si sigue en curso, ISO). */
  to: string;
  urls: Array<{
    urlId: string;
    url: string;
    site: { id: string; name: string; domain: string } | null;
    /** Respuestas a prompts (ai_search). */
    aiSearch: number;
    /** Visitas de entrenamiento (ai_training). */
    aiTraining: number;
    /** Total IA (aiSearch + aiTraining). */
    ai: number;
    /** false = la URL aún no tiene contenido publicado (sin tráfico contable). */
    published: boolean;
    /** Nota GEA (0–100), la misma que en las propuestas. null = sin nota. */
    geaScore: number | null;
  }>;
}

export interface CampaignUrl {
  id: string;
  url: string;
  relevance: number | null;
  answerCount: number | null;
  avgUrlPosition: string | null;
  prominence: number | null;
  /** Nota GEA (0-100) heredada de la propuesta, si la campaña nació de una. */
  geaScore: number | null;
  engines: string[];
  sourceType: string;
  status: string;
  /** Importe final acordado para la URL (Decimal → number|string|null). */
  finalPrice: number | string | null;
  /** Texto IA a insertar en la URL (generado/editable). */
  articleText: string | null;
  /** Periodo programado de la URL, ajustado al hueco libre (`@db.Date`). */
  startDate: string;
  endDate: string;
  createdAt: string;
  /** Soporte (medio) al que pertenece la URL; null si no está vinculada. */
  siteId: string | null;
  site: { id: string; name: string; domain: string } | null;
  /** Periodos en que estuvo activa de verdad (el plugin servía su texto), en
   *  orden. `endedAt` null = sigue activa. Vacío = nunca llegó a estar activa. */
  activePeriods: CampaignUrlActivePeriod[];
}

export interface CampaignUrlActivePeriod {
  startedAt: string;
  endedAt: string | null;
}

export interface UpdateCampaignUrlInput {
  finalPrice?: number | null;
  status?: string;
  articleText?: string;
}

/** Versión de contenido del plugin de una URL (HTML generado). */
export interface CampaignContentVersion {
  id: string;
  versionNumber: number;
  isPublished: boolean;
  publishedAt: string | null;
  htmlContent: string;
  model?: string | null;
  createdAt: string;
}

/** Contenido del plugin de una URL: estado + versiones. */
export interface CampaignContentInfo {
  id: string;
  status: string;
  currentVersionId: string | null;
  versions: CampaignContentVersion[];
}

/** Las señales y la nota GEA las calcula el backend (no se envían). */
export interface AddUrlItem {
  url: string;
  sourceType?: string;
}

/** Analítica de tráfico IA para el dashboard (datos del plugin de WordPress). */
export const analyticsApi = {
  dashboard: async (params?: {
    from?: string;
    to?: string;
    /** Site activo: acota los datos al dominio de ese site. */
    siteId?: string;
  }): Promise<import('@/types').DashboardData> => {
    const r = await apiClient.get('/analytics/dashboard', { params });
    return r.data;
  },
};

// ============================================================================
// GEOSPHERE API (lectura para el wizard)
// ============================================================================

export interface GeosphereProjectSummary {
  projectId: string;
  workspaceId: string | null;
  workspaceName: string | null;
  brandName: string;
  displayName: string | null;
  brandDomain: string;
  country: string;
  language: string;
  sector: string;
  isPaused: boolean;
  promptCount: number;
  completedRuns: number;
  createdAt: string;
  lastRunAt: string | null;
}

export interface GeosphereEngineInfo {
  id: string;
  label: string;
  isDefault?: boolean;
}

export interface GeospherePrompt {
  id: string;
  text: string;
  tags: string[];
  segment?: string;
  isActive: boolean;
  createdAt: string;
}

export interface CreatePromptInput {
  text: string;
  tags?: string[];
  segment?: string;
}

export interface UpdatePromptInput {
  text?: string;
  tags?: string[];
  segment?: string;
  isActive?: boolean;
}

export const geosphereApi = {
  listProjects: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    total: number;
    limit: number;
    offset: number;
    projects: GeosphereProjectSummary[];
  }> => {
    const r = await apiClient.get(`/geosphere/projects${buildParams(params)}`);
    return r.data;
  },
  listEngines: async (): Promise<GeosphereEngineInfo[]> => {
    const r = await apiClient.get("/geosphere/engines");
    return r.data;
  },
  /** Crea un proyecto Geosphere standalone (alta de propuesta: "crear nuevo"). */
  createProject: async (
    data: CreateGeosphereProjectInput,
  ): Promise<{
    projectId: string;
    brandName: string;
    displayName: string | null;
    brandDomain: string;
    country: string;
    language: string;
    reused: boolean;
  }> => {
    const r = await apiClient.post("/geosphere/projects", data);
    return r.data;
  },
  getProject: async (projectId: string): Promise<GeosphereProjectSummary> => {
    const r = await apiClient.get(`/geosphere/projects/${projectId}`);
    return r.data;
  },
  getPrompts: async (
    projectId: string,
  ): Promise<{ projectId: string; prompts: GeospherePrompt[] }> => {
    const r = await apiClient.get(`/geosphere/projects/${projectId}/prompts`);
    return r.data;
  },
  createPrompt: async (
    projectId: string,
    data: CreatePromptInput,
  ): Promise<GeospherePrompt> => {
    const r = await apiClient.post(
      `/geosphere/projects/${projectId}/prompts`,
      data,
    );
    return r.data;
  },
  updatePrompt: async (
    promptId: string,
    data: UpdatePromptInput,
  ): Promise<GeospherePrompt> => {
    const r = await apiClient.put(`/geosphere/prompts/${promptId}`, data);
    return r.data;
  },
  deletePrompt: async (promptId: string): Promise<void> => {
    await apiClient.delete(`/geosphere/prompts/${promptId}`);
  },
  getProjectDetails: async (
    projectId: string,
  ): Promise<{
    brandDomains: { domain: string; isPrimary: boolean }[];
    media: { domain: string; label: string | null }[];
    engines: string[];
  }> => {
    const r = await apiClient.get(`/geosphere/projects/${projectId}/details`);
    return r.data;
  },
  updateProject: async (
    projectId: string,
    data: {
      brandName?: string;
      sector?: string;
      country?: string;
      engines?: string[];
    },
  ): Promise<void> => {
    await apiClient.put(`/geosphere/projects/${projectId}`, data);
  },
  getSourceUrls: async (
    projectId: string,
    params?: { scope?: "latest" | "all"; limit?: number },
  ): Promise<{
    totalUrls: number;
    scope: string;
    urls: GeosphereSourceUrl[];
  }> => {
    const r = await apiClient.get(
      `/geosphere/projects/${projectId}/source-urls${buildParams(params)}`,
    );
    return r.data;
  },
  getScore: async (projectId: string): Promise<GeosphereScore> => {
    const r = await apiClient.get(`/geosphere/projects/${projectId}/score`);
    return r.data;
  },
  getSegmentMatrix: async (
    projectId: string,
    params?: { days?: number; from?: string; to?: string },
  ): Promise<GeosphereSegmentMatrix> => {
    const r = await apiClient.get(
      `/geosphere/projects/${projectId}/segment-matrix${buildParams(params)}`,
    );
    return r.data;
  },
  getScoreHistory: async (
    projectId: string,
    limit?: number,
  ): Promise<GeosphereScoreHistory> => {
    const r = await apiClient.get(
      `/geosphere/projects/${projectId}/score/history${buildParams({ limit })}`,
    );
    return r.data;
  },
};

// ─── Score / AIS ────────────────────────────────────────────────────────────

export interface GeosphereBrandShare {
  rank: number;
  name: string;
  /** Marca principal del proyecto (la monitorizada). */
  isMain: boolean;
  /** Score compuesto de Geosphere (0–100). null si no calculado. */
  score: number | null;
  /** % de respuestas IA que mencionan la marca (Share of Voice, 0–100). */
  presencePct: number;
  /** Posición media en la respuesta (1 = primera). Menor = mejor. */
  avgPosition: number | null;
  /** Sentimiento medio del LLM hacia la marca (0–100). Mayor = mejor. */
  avgSentiment: number | null;
  executionsWithBrand: number;
  totalMentions: number;
}

export interface GeosphereRun {
  runId: string;
  startedAt: string;
  finishedAt: string;
}

export interface GeosphereScore {
  projectId: string;
  brandName: string;
  displayName: string | null;
  run: GeosphereRun | null;
  totalExecutions: number;
  brands: GeosphereBrandShare[];
}

export interface GeosphereScoreHistoryEntry extends GeosphereRun {
  totalExecutions: number;
  brands: GeosphereBrandShare[];
}

export interface GeosphereScoreHistory {
  projectId: string;
  brandName: string;
  displayName: string | null;
  history: GeosphereScoreHistoryEntry[];
}

// ─── Segment matrix (rendimiento por segmento × marca) ──────────────────────

export interface GeosphereSegmentBrand {
  /** "main" para la marca propia, o el UUID del competidor. */
  id: string;
  name: string;
  isMain: boolean;
  domain: string | null;
}

export interface GeosphereSegmentCell {
  /** Share of Answer (presencia %). */
  soa: number;
  /** Share of Mentions (cuota de menciones) %. */
  som: number;
  /** GEO Visibility (0–100). null si no hay sentimiento. */
  geo: number | null;
  mentions: number;
  totalExecs: number;
  avgPositionScore: number | null;
  avgSentiment: number | null;
}

export interface GeosphereSegmentMatrix {
  projectId: string;
  brandName: string;
  /** Nº de días de ventana (con `days`); null cuando se pide con rango. */
  periodDays: number | null;
  /** Rango efectivo agregado (con `from`/`to`). */
  periodFrom: string | null;
  periodTo: string | null;
  runCount: number;
  hasData: boolean;
  brands: GeosphereSegmentBrand[];
  segments: string[];
  /** matrix[segment][brandId] = celda con las métricas de esa marca. */
  matrix: Record<string, Record<string, GeosphereSegmentCell>>;
}

export interface GeosphereSourceUrl {
  url: string;
  domain: string;
  citationCount: number;
  answerCount: number;
  withBrandMention: number;
  engines: string[];
  relevance: number;
  avgUrlPosition: number | null;
  avgBrandPosition: number | null;
  avgBrandSentiment: number | null;
  prominence: number | null;
  /** IDs de los prompts a los que responde la URL (texto vía getPrompts). */
  promptIds: string[];
}

/** URL candidata (Ecosistema Boosters) del módulo "URLs candidatas". */
export interface CampaignCandidateUrl {
  url: string;
  domain: string;
  /** Nota GEA (0–100), la misma que en las propuestas. Ausente en la vista
   *  del anunciante (el backend no se la envía). */
  geaScore?: number;
}

/** URL de campaña en la vista del anunciante: solo las que llegaron a estar
 *  activas, con la URL y si lo está ahora. */
export interface CampaignClientUrl {
  id: string;
  url: string;
  activeNow: boolean;
}

/** Source-url candidata a una campaña, con su nota GEA (misma que propuestas). */
export interface CampaignAvailableUrl extends GeosphereSourceUrl {
  geaScore: number;
  category: import('@/types').ProposalUrlCategory;
}

// ============================================================================
// ADVERTISERS API (anunciantes)
// ============================================================================

export interface AdvertiserInput {
  name: string;
  website?: string;
  industry?: string;
  agency?: string;
  country?: string;
  language?: import('@/types').CampaignLanguage;
  status?: import('@/types').AdvertiserStatus;
}

export const advertisersApi = {
  getAll: async (
    query?: PaginationQuery,
  ): Promise<PaginatedResponse<import('@/types').Advertiser>> => {
    const r = await apiClient.get(`/advertisers${buildParams(query)}`);
    return r.data;
  },
  /** Lista plana para selectores (p. ej. el wizard de campañas). */
  getOptions: async (): Promise<
    Pick<import('@/types').Advertiser, 'id' | 'name' | 'status' | 'language'>[]
  > => {
    const r = await apiClient.get('/advertisers/options');
    return r.data;
  },
  /** Detalle de un anunciante por id (pantalla de edición). */
  getById: async (id: string): Promise<import('@/types').Advertiser> => {
    const r = await apiClient.get(`/advertisers/${id}`);
    return r.data;
  },
  create: async (
    data: AdvertiserInput,
  ): Promise<import('@/types').Advertiser> => {
    const r = await apiClient.post('/advertisers', data);
    return r.data;
  },
  update: async (
    id: string,
    data: AdvertiserInput,
  ): Promise<import('@/types').Advertiser> => {
    const r = await apiClient.patch(`/advertisers/${id}`, data);
    return r.data;
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/advertisers/${id}`);
  },
  /** Reactiva un anunciante dado de baja. */
  restore: async (id: string): Promise<import('@/types').Advertiser> => {
    const r = await apiClient.patch(`/advertisers/${id}/restore`);
    return r.data;
  },
  /** Borrado físico permanente. */
  hardDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/advertisers/${id}/permanent`);
  },
};

// ============================================================================
// SITES API (propiedades web del publisher)
// ============================================================================

export interface SiteInput {
  name: string;
  url: string;
  domain?: string;
  title?: string;
  category?: string;
  language?: import('@/types').CampaignLanguage;
  status?: import('@/types').SiteStatus;
  /** Cuenta propietaria. `null` = sin asociar. Solo lo aplica comercial/admin. */
  userId?: string | null;
}

export const sitesApi = {
  getAll: async (
    query?: PaginationQuery & { deleted?: boolean },
  ): Promise<PaginatedResponse<import('@/types').Site>> => {
    const r = await apiClient.get(`/sites${buildParams(query)}`);
    return r.data;
  },
  /** Detalle de un site por id (pantalla de edición). */
  getById: async (id: string): Promise<import('@/types').Site> => {
    const r = await apiClient.get(`/sites/${id}`);
    return r.data;
  },
  create: async (data: SiteInput): Promise<import('@/types').Site> => {
    const r = await apiClient.post('/sites', data);
    return r.data;
  },
  update: async (
    id: string,
    data: SiteInput,
  ): Promise<import('@/types').Site> => {
    const r = await apiClient.patch(`/sites/${id}`, data);
    return r.data;
  },
  /** Baja (soft-delete). */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/sites/${id}`);
  },
  /** Reactiva un site dado de baja. */
  restore: async (id: string): Promise<import('@/types').Site> => {
    const r = await apiClient.patch(`/sites/${id}/restore`);
    return r.data;
  },
  /** Borrado físico permanente. */
  hardDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/sites/${id}/permanent`);
  },
  /** Dominios registrados activos, normalizados (para destacar URLs en el wizard). */
  listDomains: async (): Promise<string[]> => {
    const r = await apiClient.get('/sites/domains');
    return r.data;
  },
};

// ============================================================================
// SETTINGS API (configuración del sistema)
// ============================================================================

export interface CampaignSettings {
  defaultMarginPct: number;
  urlMinPrice: number;
  /** Margen comercial (%) para el Precio Cliente de las propuestas. */
  commercialMarginPct: number;
  /** Margen de agencia (%) para el Precio Cliente de las propuestas. */
  agencyMarginPct: number;
}

export const settingsApi = {
  getCampaign: async (): Promise<CampaignSettings> => {
    const r = await apiClient.get('/settings/campaign');
    return r.data;
  },
  updateCampaign: async (
    data: Partial<CampaignSettings>,
  ): Promise<CampaignSettings> => {
    const r = await apiClient.put('/settings/campaign', data);
    return r.data;
  },
};

// ============================================================================
// PROPOSALS API (Propuestas: valoración GEA de URLs por cliente)
// ============================================================================

export interface CreateProposalInput {
  advertiserId: string;
  name: string;
  geosphereProjectId?: string;
  currency?: string;
}

export interface AddProposalUrlsResult {
  added: number;
  skipped: string[];
  notFound: string[];
  proposal: import('@/types').Proposal;
}

export const proposalsApi = {
  getAll: async (query?: {
    advertiserId?: string;
    status?: string;
  }): Promise<import('@/types').ProposalListItem[]> => {
    const r = await apiClient.get(`/proposals${buildParams(query)}`);
    return r.data;
  },
  getOne: async (id: string): Promise<import('@/types').Proposal> => {
    const r = await apiClient.get(`/proposals/${id}`);
    return r.data;
  },
  create: async (
    data: CreateProposalInput,
  ): Promise<import('@/types').Proposal> => {
    const r = await apiClient.post('/proposals', data);
    return r.data;
  },
  update: async (
    id: string,
    data: { name?: string; status?: string },
  ): Promise<import('@/types').Proposal> => {
    const r = await apiClient.patch(`/proposals/${id}`, data);
    return r.data;
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/proposals/${id}`);
  },
  /** Añade URLs; el backend las valora con GEA. */
  addUrls: async (
    id: string,
    urls: string[],
  ): Promise<AddProposalUrlsResult> => {
    const r = await apiClient.post(`/proposals/${id}/urls`, { urls });
    return r.data;
  },
  removeUrl: async (
    id: string,
    urlId: string,
  ): Promise<import('@/types').Proposal> => {
    const r = await apiClient.delete(`/proposals/${id}/urls/${urlId}`);
    return r.data;
  },
  /** Descarga la propuesta como fichero .xlsx (blob). */
  exportXlsx: async (id: string): Promise<Blob> => {
    const r = await apiClient.get(`/proposals/${id}/export`, {
      responseType: 'blob',
    });
    return r.data;
  },
  /** Envía a los medios las URLs nuevas (con Site y sin notificar). */
  notifyPublishers: async (
    id: string,
  ): Promise<{
    notified: number;
    publishers: number;
    withoutSite: number;
  }> => {
    const r = await apiClient.post(`/proposals/${id}/notify-publishers`);
    return r.data;
  },
  /** (Editor) URLs de propuesta de sus Sites. */
  listPublisherUrls: async (): Promise<
    import('@/types').PublisherProposalUrl[]
  > => {
    const r = await apiClient.get('/proposals/publisher-urls');
    return r.data;
  },
  /** (Editor) Acepta/rechaza/contraoferta una URL de propuesta suya. */
  decidePublisherUrl: async (
    urlId: string,
    decision: 'accepted' | 'rejected' | 'countered',
    counterPrice?: number,
    note?: string,
  ): Promise<{ id: string; status: string }> => {
    const r = await apiClient.patch(
      `/proposals/publisher-urls/${urlId}/decision`,
      { decision, ...(counterPrice != null ? { counterPrice } : {}), ...(note ? { note } : {}) },
    );
    return r.data;
  },
  /** Candidatas del proyecto con GEA calculada + dominio registrado. */
  availableUrls: async (
    id: string,
  ): Promise<{ urls: import('@/types').ProposalAvailableUrl[] }> => {
    const r = await apiClient.get(`/proposals/${id}/available-urls`);
    return r.data;
  },
  /** Visitas IA (30 días) por URL añadida. Carga lazy: consulta cara aparte. */
  aiVisits: async (id: string): Promise<Record<string, number>> => {
    const r = await apiClient.get(`/proposals/${id}/ai-visits`);
    return r.data;
  },
  /** Fija el precio de una URL (antes de enviar, o respondiendo a contraoferta). */
  setUrlPrice: async (
    id: string,
    urlId: string,
    price: number,
    note?: string,
  ): Promise<import('@/types').Proposal> => {
    const r = await apiClient.patch(`/proposals/${id}/urls/${urlId}/price`, {
      price,
      ...(note ? { note } : {}),
    });
    return r.data;
  },
  /** Acepta la contraoferta del medio para una URL (cierra la negociación). */
  acceptCounterOffer: async (
    id: string,
    urlId: string,
  ): Promise<import('@/types').Proposal> => {
    const r = await apiClient.post(
      `/proposals/${id}/urls/${urlId}/accept-counter`,
    );
    return r.data;
  },
  /** Historial de ofertas/contraofertas de una URL. */
  urlOffers: async (
    id: string,
    urlId: string,
  ): Promise<import('@/types').ProposalUrlOffer[]> => {
    const r = await apiClient.get(`/proposals/${id}/urls/${urlId}/offers`);
    return r.data;
  },
  /** Re-valora todas las URLs con la config GEA actual (solo en borrador). */
  recalculate: async (id: string): Promise<import('@/types').Proposal> => {
    const r = await apiClient.post(`/proposals/${id}/recalculate`);
    return r.data;
  },
  /** Config editable de la GEA (vive en /settings/gea). */
  getGeaConfig: async (): Promise<import('@/types').GeaConfig> => {
    const r = await apiClient.get('/settings/gea');
    return r.data;
  },
  updateGeaConfig: async (
    config: import('@/types').GeaConfig,
  ): Promise<import('@/types').GeaConfig> => {
    const r = await apiClient.put('/settings/gea', config);
    return r.data;
  },
};

// ============================================================================
// BILLING API — Facturación por soporte y mes natural (solo admins/superadmins)
// ============================================================================

export interface BillingLine {
  soporteId: string;
  soporteName: string;
  soporteEmail: string;
  campaignId: string;
  campaignName: string;
  url: string;
  /** Periodo programado de la URL (ISO). */
  startDate: string;
  endDate: string;
  /** Mes natural (YYYY-MM) del fin (mes de facturación). */
  month: string;
  amount: number;
}

export const billingApi = {
  summary: async (params?: {
    from?: string;
    to?: string;
  }): Promise<BillingLine[]> => {
    const q: Record<string, string> = {};
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    const r = await apiClient.get('/billing/summary', { params: q });
    return r.data;
  },
  exportXlsx: async (params?: {
    from?: string;
    to?: string;
  }): Promise<Blob> => {
    const q: Record<string, string> = {};
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    const r = await apiClient.get('/billing/export', {
      params: q,
      responseType: 'blob',
    });
    return r.data;
  },
};

// ============================================================================
// EMAIL OUTBOX API (Sistema → Correos, solo lectura)
// ============================================================================

export const emailOutboxApi = {
  getAll: async (
    query?: PaginationQuery,
  ): Promise<PaginatedResponse<import('@/types').EmailOutboxListItem>> => {
    const r = await apiClient.get(`/email-outbox${buildParams(query)}`);
    return r.data;
  },
  stats: async (
    query?: PaginationQuery,
  ): Promise<import('@/types').EmailOutboxStats> => {
    const r = await apiClient.get(`/email-outbox/stats${buildParams(query)}`);
    return r.data;
  },
  getById: async (
    id: string,
  ): Promise<import('@/types').EmailOutboxDetail> => {
    const r = await apiClient.get(`/email-outbox/${id}`);
    return r.data;
  },
};

// ============================================================================
// SSO API (login central mentions.boosters.es)
// ============================================================================

/** Otra app del usuario en el login central (enlace de la sidebar). */
export interface SsoApp {
  client_id: string;
  name: string;
  login_uri: string;
}

export const ssoApi = {
  /** ¿Está activo el login central en este entorno y dónde está? (público) */
  status: async (): Promise<{ enabled: boolean; mentionsUrl: string | null }> => {
    const response = await apiClient.get('/sso/status');
    return response.data;
  },

  /** Otras apps del usuario asociado (vacío si no está asociado). */
  apps: async (): Promise<SsoApp[]> => {
    const response = await apiClient.get('/sso/apps');
    return response.data;
  },

  /** Abre la sesión de GEA desde la cookie que dejó el callback SSO. */
  refreshSession: async (): Promise<{ accessToken: string }> => {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  },

  /** URL que inicia el login central (navegación completa, no XHR). */
  startUrl: (returnTo = '/'): string =>
    `${apiClient.defaults.baseURL}/sso/start?return_to=${encodeURIComponent(returnTo)}`,
};
